/*
 * The sky is rolled, not told. Every full page load picks a time of day and a
 * landscape at random (or takes them from ?phase= and ?scene= for that load),
 * stamps them on <html>, and never stores anything.
 *
 * Everything in this file is a self-contained ES5 function: no closures over
 * module scope, no spread, classes or async. BOOT_SCRIPT (at the bottom) is
 * built from these functions' source text and inlined into <head> by
 * gatsby-ssr.js, so the palette and landscape are on <html> before first
 * paint. The React store (useSky) calls the same functions afterwards.
 */

export var PHASES = ['dawn', 'day', 'golden', 'dusk', 'night']
export var SCENES = ['bay', 'desert', 'forest']

export function nextPhase(phase) {
  var ORDER = ['dawn', 'day', 'golden', 'dusk', 'night']
  var i = ORDER.indexOf(phase)
  return ORDER[(i + 1) % ORDER.length]
}

/** A time of day named in the URL (`?phase=dusk`), for this load only; else null. */
export function readPhase(search) {
  var VALID = ['dawn', 'day', 'golden', 'dusk', 'night']
  var m = /[?&]phase=([a-z]+)/.exec(search || '')
  return m && VALID.indexOf(m[1]) !== -1 ? m[1] : null
}

/** A landscape named in the URL (`?scene=bay`), for this load only; else null. */
export function readScene(search) {
  var VALID = ['bay', 'desert', 'forest']
  var m = /[?&]scene=([a-z]+)/.exec(search || '')
  return m && VALID.indexOf(m[1]) !== -1 ? m[1] : null
}

/**
 * A sky for a phase: the sun somewhere plausible for that hour (0 on the
 * horizon, 1 at its high point; at night the same number is the moon's
 * height) and how strong the stars are.
 */
export function skyFor(phase) {
  var RANGE = { dawn: [0.05, 0.25], day: [0.6, 1], golden: [0.15, 0.4], dusk: [0, 0.08], night: [0.5, 1] }
  var r = RANGE[phase] || RANGE.golden
  return {
    phase: phase,
    sunAlt: r[0] + Math.random() * (r[1] - r[0]),
    stars: phase === 'night' ? 1 : phase === 'dusk' ? 0.4 : 0,
  }
}

/**
 * The time of day for this page load: the URL's if it names one, otherwise
 * a random one with the sun up. Rolled once and stamped on <html>
 * (data-phase plus the sun height) so every later read agrees; the server's
 * default stamp is not a roll, hence the data-rolled marker.
 */
export function choosePhase(search, root, readPhase, skyFor) {
  var VALID = ['dawn', 'day', 'golden', 'dusk', 'night']
  var named = readPhase(search)
  var stamped = root.getAttribute('data-phase')
  var rolled = root.getAttribute('data-rolled') === '1'
  var sky
  if (named) {
    sky = skyFor(named)
  } else if (rolled && stamped && VALID.indexOf(stamped) !== -1) {
    sky = skyFor(stamped)
    var kept = parseFloat(root.getAttribute('data-sun-alt'))
    if (!isNaN(kept)) sky.sunAlt = kept
  } else {
    // A first roll always shows the sun: no dusk, and the disc well clear
    // of the horizon. The click and ?phase= still reach every phase.
    var FIRST = ['dawn', 'day', 'golden', 'night']
    sky = skyFor(FIRST[Math.floor(Math.random() * FIRST.length)])
    sky.sunAlt = 0.5 + Math.random() * 0.45
  }
  root.setAttribute('data-phase', sky.phase)
  root.setAttribute('data-sun-alt', sky.sunAlt.toFixed(3))
  root.setAttribute('data-rolled', '1')
  return sky
}

/** The landscape for this page load: the URL's if it names one, else random, rolled once. */
export function chooseScene(search, root, readScene) {
  var VALID = ['bay', 'desert', 'forest']
  var scene = readScene(search)
  if (!scene) {
    var stamped = root.getAttribute('data-scene')
    scene = stamped && VALID.indexOf(stamped) !== -1
      ? stamped
      : VALID[Math.floor(Math.random() * VALID.length)]
  }
  root.setAttribute('data-scene', scene)
  return scene
}

/**
 * Scatters the three cloud banks once per page load, as CSS variables on
 * <html>, so no two loads frame up quite the same and the positions are in
 * place before first paint. Kept within bounds that hold them in the sky.
 */
export function scatterClouds(root) {
  if (root.getAttribute('data-clouds') === '1') return
  var names = ['--cloud-a', '--cloud-b', '--cloud-c']
  for (var i = 0; i < names.length; i++) {
    var dx = (Math.random() * 2 - 1) * 190
    var dy = (Math.random() * 2 - 1) * 68
    root.style.setProperty(names[i], dx.toFixed(1) + 'px ' + dy.toFixed(1) + 'px')
  }
  root.setAttribute('data-clouds', '1')
}

/** Writes the sky onto <html>, outside the React root, so hydration never sees it. */
export function applySky(doc, sky) {
  var SKY0 = { dawn: '#0f3358', day: '#163f6f', golden: '#24284f', dusk: '#0f1233', night: '#05070f' }
  var html = doc.documentElement
  html.setAttribute('data-phase', sky.phase)
  html.style.setProperty('--sun-alt', sky.sunAlt.toFixed(3))
  html.style.setProperty('--stars', String(sky.stars))
  var meta = doc.querySelector ? doc.querySelector('meta[name="theme-color"]') : null
  if (meta) meta.content = SKY0[sky.phase] || SKY0.golden
}

function boot(readPhase, readScene, skyFor, choosePhase, chooseScene, scatterClouds, applySky) {
  try {
    var root = document.documentElement
    var sky = choosePhase(location.search, root, readPhase, skyFor)
    chooseScene(location.search, root, readScene)
    scatterClouds(root)
    applySky(document, sky)
  } catch (e) { /* the CSS defaults (golden hour, the bay) stand */ }
}

export var BOOT_SCRIPT = '(' + boot.toString() + ')('
  + readPhase.toString() + ','
  + readScene.toString() + ','
  + skyFor.toString() + ','
  + choosePhase.toString() + ','
  + chooseScene.toString() + ','
  + scatterClouds.toString() + ','
  + applySky.toString() + ')'
